import { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { useAuth } from "./AuthContext";

export default function Submissions() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("contact");
  const filterRef = useRef("contact");
  const { user } = useAuth();

  const load = (kind) =>
    api.get(`/admin/submissions/all?kind=${kind}`).then((r) => {
      if (kind === filterRef.current) setItems(r.data);
    });

  useEffect(() => {
    filterRef.current = filter;
    setItems([]);
    load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/submissions/all/${id}`);
      toast.success("Deleted");
      setItems((prev) => prev.filter((s) => s.id !== id));
      load(filterRef.current);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div data-testid="admin-submissions-page">
      <p className="overline">Inbox</p>
      <h1 className="display-2 mt-4">Submissions</h1>

      <div className="mt-8 flex gap-1">
        {[
          ["contact", "Enquiries"],
          ["proposal", "Proposals"],
          ["follow", "Milestone followers"],
          ["newsletter", "Newsletter"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`submissions-tab-${k}`}
            className={`px-5 py-2.5 text-[0.68rem] font-bold uppercase tracking-[0.16em] transition-colors ${
              filter === k ? "bg-maasai text-white" : "text-zinc-500 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-3" data-testid="submissions-list">
        {items.length === 0 ? (
          <p className="body-copy">Nothing here yet.</p>
        ) : (
          items.map((s) => (
            <div key={s.id} className="border border-zinc-900 bg-ink-surface p-5" data-testid={`submission-${s.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-base uppercase tracking-wide text-white">
                    {s.name || s.email}
                  </p>
                  <p className="mt-1 text-xs text-zinc-500">
                    {s.email}
                    {s.organisation ? ` · ${s.organisation}` : ""}
                    {s.subject ? ` · ${s.subject}` : ""}
                    {s.kind === "follow" ? ` · Step ${s.plan_order}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[0.62rem] uppercase tracking-[0.16em] text-zinc-600">
                    {new Date(s.created_at).toLocaleDateString()}
                  </span>
                  {user?.role === "super_admin" && (
                    <button onClick={() => remove(s.id)} aria-label="Delete submission" data-testid={`delete-submission-${s.id}`} className="border border-zinc-800 p-2 text-zinc-500 hover:border-maasai hover:text-maasai">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
              {s.message ? <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-zinc-300">{s.message}</p> : null}
              {s.kind === "proposal" ? (
                <dl className="mono mt-4 grid gap-3 border-t border-zinc-900 pt-4 text-xs sm:grid-cols-4" data-testid={`proposal-meta-${s.id}`}>
                  {[
                    ["County", s.county],
                    ["Indicative cost", s.estimated_cost],
                    ["Timeline", s.timeline],
                    ["Country", s.country],
                  ]
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-[0.56rem] uppercase tracking-[0.2em] text-zinc-600">{k}</dt>
                        <dd className="mt-1 text-zinc-300">{v}</dd>
                      </div>
                    ))}
                  {s.link ? (
                    <div className="sm:col-span-4">
                      <dt className="text-[0.56rem] uppercase tracking-[0.2em] text-zinc-600">Link</dt>
                      <dd className="mt-1">
                        <a href={s.link} target="_blank" rel="noreferrer" className="text-ochre underline underline-offset-4">
                          {s.link}
                        </a>
                      </dd>
                    </div>
                  ) : null}
                </dl>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
